/**
 * Tiny DOM event emitter for leaderboard beats. We dispatch CustomEvents on
 * `window` so any future coach-side soundboard, analytics hook, or LED
 * controller can listen without us plumbing callbacks through the render
 * tree. The TV stays silent by default — consumers opt in.
 *
 * Event names are stable; payloads are versioned implicitly by field
 * presence (new fields optional).
 */

export type TvEventMap = {
  'tv:rank1-swap': {
    prevAthleteId: string | null
    nextAthleteId: string
    athleteName: string
    score: number
  }
  'tv:milestone': {
    athleteId: string
    athleteName: string
    threshold: number
    score: number
  }
  'tv:final-30s': { sessionId: string }
  'tv:session-ended': { sessionId: string }
  'tv:session-started': { sessionId: string }
  'tv:overtake': {
    overtakerId: string
    overtakerName: string
    overtakenId: string
    overtakenName: string
    newRank: number
  }
}

export function emit<K extends keyof TvEventMap>(name: K, detail: TvEventMap[K]): void {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }))
  } catch {
    /* server-render or sandboxed env — silently ignore */
  }
}

export function on<K extends keyof TvEventMap>(
  name: K,
  handler: (detail: TvEventMap[K]) => void,
): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<TvEventMap[K]>).detail)
  window.addEventListener(name, listener)
  return () => window.removeEventListener(name, listener)
}
